import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Banner, Card, Button, Spinner } from '../../components/serac';
import MarketingHeader from '../../components/common/MarketingHeader';
import MarketingFooter from '../../components/common/MarketingFooter';
import { apiClient } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';

type PaymentStatus = 'loading' | 'success' | 'pending' | 'failed';

export default function PaymentComplete() {
  const { t } = useTranslation(['billing', 'common']);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshPortalSession, isPortalAuthenticated } = useAuth();
  const [status, setStatus] = useState<PaymentStatus>('loading');
  const [sessionRefreshed, setSessionRefreshed] = useState(false);
  const [countdown, setCountdown] = useState(5);

  // Detect if coming from onboarding flow
  const isFromOnboarding = useMemo(() => {
    if (searchParams.get('source') === 'onboarding') return true;
    const stored = localStorage.getItem('serac_onboarding_checkout');
    if (!stored) return false;
    try {
      const data = JSON.parse(stored);
      return data.fromOnboarding && Date.now() - data.timestamp < 3600000;
    } catch { return false; }
  }, [searchParams]);

  // Fetch subscription status
  const { data: subscriptionData, isLoading, error } = useQuery({
    queryKey: ['subscription-status'],
    queryFn: () => apiClient.portalGetSubscription(),
    refetchInterval: status === 'pending' ? 3000 : false, // Poll if pending
    enabled: isPortalAuthenticated,
  });

  // Determine status based on subscription data
  useEffect(() => {
    if (isLoading) {
      setStatus('loading');
      return;
    }

    if (error) {
      setStatus('failed');
      return;
    }

    if (subscriptionData?.hasSubscription) {
      const subStatus = subscriptionData.subscription?.status;
      if (subStatus === 'trialing' || subStatus === 'active') {
        setStatus('success');
      } else if (subStatus === 'past_due' || subStatus === 'unpaid') {
        setStatus('failed');
      } else {
        setStatus('pending');
      }
    } else {
      // No subscription yet, might still be processing
      setStatus('pending');
    }
  }, [subscriptionData, isLoading, error]);

  // Refresh session when successful to get updated subscription/module data
  useEffect(() => {
    if (status === 'success' && !sessionRefreshed) {
      (async () => {
        await refreshPortalSession();
        setSessionRefreshed(true);
      })();
    }
  }, [status, sessionRefreshed, refreshPortalSession]);

  // Only start countdown AFTER session is refreshed
  useEffect(() => {
    if (status === 'success' && sessionRefreshed && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (status === 'success' && sessionRefreshed && countdown === 0) {
      localStorage.removeItem('serac_onboarding_checkout');
      if (isFromOnboarding) {
        navigate('/portal/dashboard?resume_onboarding=complete');
      } else {
        navigate('/portal/dashboard');
      }
    }
  }, [status, sessionRefreshed, countdown, navigate]);

  // Redirect if not authenticated
  useEffect(() => {
    if (!isPortalAuthenticated) {
      navigate('/');
    }
  }, [isPortalAuthenticated, navigate]);

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <div className="text-center py-12">
            <div className="flex justify-center mb-6">
              <Spinner size="lg" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {t('billing:paymentComplete.processing')}
            </h2>
            <p className="text-gray-600">
              {t('billing:paymentComplete.processingHint')}
            </p>
          </div>
        );

      case 'success':
        return (
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {t('billing:paymentComplete.success')}
            </h2>
            <p className="text-gray-600 mb-6">
              {t('billing:paymentComplete.thanks')}
            </p>

            <div style={{ maxWidth: 28 * 16, margin: '0 auto 24px' }}>
              <Banner tone="success" title={t('billing:paymentComplete.whatsNext')}>
                <ul style={{ textAlign: 'left', listStyle: 'none', padding: 0, margin: 0 }}>
                  <li>&#10003; {t('billing:paymentComplete.step1')}</li>
                  <li>&#10003; {t('billing:paymentComplete.step2')}</li>
                  <li>&#10003; {t('billing:paymentComplete.step3')}</li>
                </ul>
              </Banner>
            </div>

            <p className="text-sm text-gray-500 mb-4">
              {sessionRefreshed
                ? t('billing:paymentComplete.redirecting', { count: countdown })
                : t('billing:paymentComplete.settingUp')}
            </p>

            <Button variant="ice" disabled={!sessionRefreshed} onClick={() => {
              localStorage.removeItem('serac_onboarding_checkout');
              navigate(isFromOnboarding ? '/portal/dashboard?resume_onboarding=complete' : '/portal/dashboard');
            }}>
              {!sessionRefreshed
                ? t('billing:paymentComplete.preparing')
                : isFromOnboarding ? t('billing:paymentComplete.continueSetup') : t('billing:paymentComplete.goToDashboard')}
            </Button>
          </div>
        );

      case 'pending':
        return (
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {t('billing:paymentComplete.pendingTitle')}
            </h2>
            <p className="text-gray-600 mb-6">
              {t('billing:paymentComplete.pendingHint')}
            </p>
            <div className="flex justify-center">
              <div className="animate-pulse flex space-x-2">
                <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                <div className="w-2 h-2 bg-blue-600 rounded-full animation-delay-150"></div>
                <div className="w-2 h-2 bg-blue-600 rounded-full animation-delay-300"></div>
              </div>
            </div>
          </div>
        );

      case 'failed':
        return (
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {t('billing:paymentComplete.failedTitle')}
            </h2>
            <p className="text-gray-600 mb-6">
              {t('billing:paymentComplete.failedHint')}
            </p>

            <div className="space-y-3">
              <Button variant="ice" onClick={() => {
                if (isFromOnboarding) {
                  localStorage.removeItem('serac_onboarding_checkout');
                  navigate('/portal/dashboard');
                } else {
                  navigate('/checkout');
                }
              }}>
                {t('billing:paymentComplete.tryAgain')}
              </Button>
              <p className="text-sm text-gray-500">
                {t('billing:paymentComplete.needHelp')}{' '}
                <a href="mailto:support@serac.build" className="text-blue-600 hover:underline">
                  support@serac.build
                </a>
              </p>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <MarketingHeader />

      <div className="max-w-2xl mx-auto px-4 py-12 sm:px-6 lg:px-8 flex-1">
        <Card>
          {renderContent()}
        </Card>

        {status === 'success' && subscriptionData?.subscription && (
          <Card className="mt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('billing:paymentComplete.subscriptionDetails')}</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">{t('billing:plan.label')}</span>
                <p className="font-medium text-gray-900 capitalize">
                  {subscriptionData.subscription.plan}
                </p>
              </div>
              <div>
                <span className="text-gray-500">{t('billing:invoice.status')}</span>
                <p className="font-medium text-green-600 capitalize">
                  {subscriptionData.subscription.status}
                </p>
              </div>
              <div>
                <span className="text-gray-500">{t('billing:summary.billing')}</span>
                <p className="font-medium text-gray-900 capitalize">
                  {subscriptionData.subscription.billingInterval}
                </p>
              </div>
              <div>
                <span className="text-gray-500">{t('billing:paymentComplete.trialEnds')}</span>
                <p className="font-medium text-gray-900">
                  {subscriptionData.subscription.currentPeriodEnd
                    ? new Date(subscriptionData.subscription.currentPeriodEnd).toLocaleDateString()
                    : '-'}
                </p>
              </div>
            </div>
          </Card>
        )}
      </div>
      <MarketingFooter />
    </div>
  );
}
